import { useAuth0 } from "@auth0/auth0-react"
import classNames from "classnames"
import { useLocation, useNavigate } from "react-router-dom"
import { RadioButton } from "primereact/radiobutton"
import { Dropdown } from "primereact/dropdown"
import { MultiSelect } from "primereact/multiselect"
import { isAxiosError } from "axios"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Toast } from "primereact/toast"
import { Tooltip } from "primereact/tooltip"
import { myCustomersQueryKey, useSetCurrentCustomer } from "api/useMyCustomers"
import { clearCustomerScopedCaches } from "api/bootstrapQuery"
import useAuth0Metadata from "api/useAuth0Metadata"
import { useTheme } from "context/theme-provider"
import type { CustomerId } from "types/customer"
import { useCustomerData } from "context/customer-provider"
import { useQueryClient } from "@tanstack/react-query"
import { clearBootSnapshot } from "boot/snapshot"
import { useNRControlDesk } from "context/control-desk-provider"
import orderBy from "lodash/orderBy"
import { getRoutes } from "utils/routes"
import { openableRoutes, pathAfterCustomerSwitch } from "utils/routes/paths"
import AppBreadcrumb from "./AppBreadcrumb"
import { MENU_SLIDE_MS, useCanHover, useFirstHideHint, useIsDesktopMenu } from "./compactMenu"
import MaintenanceNotice from "./MaintenanceNotice"
import NotificationBell from "./NotificationBell"
import WhatsNewButton from "./WhatsNewButton"
import styles from "./AppTopbar.module.scss"

type Props = {
  topbarUserMenuActive: boolean
  /** The side menu is tucked away for more space: ☰ then reads "Menu" so it's clear where it went. */
  menuHidden: boolean
  /** Below 992px the menu is an overlay drawer that ☰ opens and closes. */
  mobileMenuOpen: boolean
  // eslint-disable-next-line no-undef
  onMenuButtonClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  // eslint-disable-next-line no-undef
  onTopbarUserMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const AppTopbar = ({ topbarUserMenuActive, menuHidden, mobileMenuOpen, onMenuButtonClick, onTopbarUserMenu }: Props) => {
  const { user, logout } = useAuth0()
  const { theme, changeTheme } = useTheme()
  const { customerId, setCustomerId, availableCustomers, customerResolved } = useCustomerData()
  const { controlDesks, allowedDesks, homeDesk, setControlDesks } = useNRControlDesk()
  const queryClient = useQueryClient()
  const setCurrentCustomer = useSetCurrentCustomer()
  const toastRef = useRef<Toast>(null)

  // ART-3130: the picker lists everything /me/customers says the account may
  // use (memberships ∪ home customer ∪ all for super-admins) and only renders
  // when there is a choice — a single-customer user sees no picker, as before.
  const customerOptions = orderBy(availableCustomers, ["customer_id"], ["asc"]).map((customer) => ({
    value: customer.customer_id,
    label: customer.customer_id,
    title: customer.title,
  }))
  const userCanSwapCustomerId = customerOptions.length > 1

  // A /me/customers list from the server (initial, focus refetch, or after a
  // refused switch) that does not contain the ACTIVE customer means the
  // gateway will refuse it — the only ways in are the home claim and a
  // membership row, and the list is exactly those. Move to the home customer
  // (or the first available) and say so, instead of leaving a picker with no
  // value and a page of 401s. This includes the first list of a session, so
  // a revoke that landed while the user was logged out is caught on load.
  // Two exclusions: the home claim itself (the gateway admits it even when
  // config.customer lacks the row and the server omits it), and the
  // degraded fallback list ("legacy" entries), which is never authoritative.
  const { appMetadata, userIsSuperAdmin } = useAuth0Metadata()
  const navigate = useNavigate()
  const location = useLocation()
  // Switching customer lands on the same page when the new customer's app has
  // it, else on its home ("/" for Network Rail, "/dashboard" for roads) —
  // never a path it would render blank. Always without the old query string.
  const goToCustomer = (id: CustomerId) => {
    const routesOf = (customer: CustomerId) => openableRoutes(getRoutes(customer, !!userIsSuperAdmin, controlDesks), !!userIsSuperAdmin)
    const pathname = pathAfterCustomerSwitch({ pathname: location.pathname, from: routesOf(customerId), to: routesOf(id) })
    navigate({ pathname, search: "" }, { replace: pathname === location.pathname })
  }
  const homeCustomerId = appMetadata?.customerID
  useEffect(() => {
    // Before the CustomerProvider has resolved (a boot snapshot's customer
    // awaiting the server) the resolution itself moves the user.
    if (!customerResolved) return
    if (availableCustomers.length === 0 || availableCustomers.some((customer) => customer.source === "legacy")) return
    const ids = new Set<string>(availableCustomers.map((customer) => customer.customer_id))
    if (!customerId || ids.has(customerId) || customerId === homeCustomerId) return
    // the default customer (its entry is always "home"), else the first
    const fallback =
      availableCustomers.find((customer) => customer.source === "home")?.customer_id ?? availableCustomers[0].customer_id
    toastRef.current?.show({
      severity: "warn",
      summary: "Access removed",
      detail: `Your access to ${customerId} was removed. Switched to ${fallback}.`,
      life: 8000,
    })
    setCustomerId(fallback)
    clearCustomerScopedCaches(queryClient)
    goToCustomer(fallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCustomers, customerId, homeCustomerId, customerResolved])

  const onCustomerIdChange = async (id: CustomerId) => {
    if (!id || id === customerId) return
    // Persist the choice in rr-api, then switch locally. No Auth0 write and
    // no token re-mint: the gateway authorises the new customer from the
    // membership row, not from the token, so the switch is immediate.
    // A 403/404 means "you may no longer use this customer" (revoked since
    // the list was fetched): stay put, say so, refresh the list. Any other
    // failure only loses the remembered selection — still switch.
    try {
      await setCurrentCustomer.mutateAsync(id)
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status === 403 || status === 404) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Customer not available",
          detail: `You no longer have access to ${id}. The customer list has been refreshed.`,
          life: 6000,
        })
        queryClient.invalidateQueries({ queryKey: myCustomersQueryKey })
        return
      }
      console.warn("could not persist customer selection", error)
    }
    setCustomerId(id)
    clearCustomerScopedCaches(queryClient)
    goToCustomer(id)
  }

  const profileItemClassName = classNames("profile-item", {
    "active-menuitem fadeInDown": topbarUserMenuActive,
  })

  // "Where did the menu go?" — under ☰ on the first hide of a page load. In a
  // portal: the top bar is its own low stacking context, which page content
  // (maps, sticky headers) and a peeking menu would paint over.
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  // ☰ hides / shows the side menu on desktop; below 992px it opens / closes the drawer
  const desktopMenu = useIsDesktopMenu()
  // touch screens get no hover tooltip, and no "rest the pointer on the edge" advice
  const canHover = useCanHover()
  const desktopMenuLabel = menuHidden ? "Show menu" : "Hide menu"
  const drawerLabel = mobileMenuOpen ? "Close menu" : "Open menu"
  const menuHintVisible = useFirstHideHint(menuHidden)
  const [menuHintAt, setMenuHintAt] = useState<{ top: number; left: number } | null>(null)
  useEffect(() => {
    setMenuHintAt(null)
    if (!menuHintVisible) return undefined
    const place = () => {
      const rect = menuButtonRef.current?.getBoundingClientRect()
      if (rect) setMenuHintAt({ top: rect.bottom + 12, left: rect.left })
    }
    // ☰ slides left with the page for the theme's 0.4s as the menu hides:
    // point at where it ends up
    const settled = window.setTimeout(place, MENU_SLIDE_MS + 50)
    window.addEventListener("resize", place)
    return () => {
      window.clearTimeout(settled)
      window.removeEventListener("resize", place)
    }
  }, [menuHintVisible])

  return (
    <div className={`${styles.rrTopBar} layout-topbar`} style={{ zIndex: 4 }}>
      <Toast ref={toastRef} />
      <div className={`md:topbar-left ${styles.topbarLeft}`}>
        <div className="flex">
          <button
            ref={menuButtonRef}
            type="button"
            className={classNames("menu-button p-link", styles.menuToggle, { [styles.menuToggleLabelled]: menuHidden })}
            onClick={onMenuButtonClick}
            aria-label={desktopMenu ? desktopMenuLabel : drawerLabel}
            aria-expanded={desktopMenu ? !menuHidden : mobileMenuOpen}
            aria-controls="sidebar-menu"
            data-pr-tooltip={menuHidden ? "Show menu — or press [" : "Hide menu for more space — or press ["}
            data-pr-position="bottom"
          >
            {/* a span, not <i>: the theme swaps `.menu-button i` for a › glyph while the menu is hidden */}
            <span className="pi pi-bars" aria-hidden="true" />
            {menuHidden && <span className={styles.menuToggleLabel}>Menu</span>}
          </button>
          {/* the hint takes the tooltip's place under ☰ while it shows */}
          {desktopMenu && canHover && !menuHintVisible && <Tooltip target={`.${styles.menuToggle}`} showDelay={400} />}
          {menuHintAt &&
            createPortal(
              <div className={styles.menuHint} role="status" style={menuHintAt}>
                {canHover ? (
                  <>
                    <strong>Menu hidden.</strong> Click <strong>☰ Menu</strong> or press <kbd>[</kbd> to bring it back. To jump to
                    another page, rest the pointer on the left edge of the screen.
                  </>
                ) : (
                  <>
                    <strong>Menu hidden.</strong> Tap <strong>☰ Menu</strong> to bring it back.
                  </>
                )}
              </div>,
              document.body,
            )}
          <span className="topbar-separator" />

          <div
            className="layout-breadcrumb viewname flex align-items-center mt-2"
            style={{ textTransform: "uppercase" }}
          >
            <AppBreadcrumb />
          </div>
        </div>
      </div>
      <MaintenanceNotice variant="topbar" />
      <div className={`md:topbar-right ${styles.topbarRight}`}>
        <ul className="topbar-menu">
          {userCanSwapCustomerId && (
            <li id="customer-id-select" data-testid="customer-id-select" className="mr-2 lg:mr-0">
              <Dropdown
                id="customer-select-dropdown"
                value={customerId}
                options={customerOptions}
                optionLabel="label"
                optionValue="value"
                filter={customerOptions.length > 10}
                filterBy="label,title"
                itemTemplate={(option: (typeof customerOptions)[number]) => (
                  <div className="flex flex-column">
                    <span>{option.label}</span>
                    <small style={{ opacity: 0.7 }}>{option.title}</small>
                  </div>
                )}
                onChange={(e) => onCustomerIdChange(e.value)}
              />
            </li>
          )}
          {/* NR control desks (ART-3177): tick one or more of the desks this
              user may view (own desk + super-admin-granted, or every desk for
              ALL accounts); every NR page shows their union. Hidden when
              there's nothing to choose. */}
          {allowedDesks.length > 1 && (
            <li data-testid="control-desk-id-select" className="mr-2 lg:mr-0">
              <MultiSelect
                id="control-desk-select-dropdown"
                value={controlDesks}
                options={allowedDesks}
                // Clearing everything (Deselect all, or un-ticking the last desk)
                // falls back to the user's own desk — the provider never
                // leaves the selection empty.
                onChange={(e) => setControlDesks(Array.isArray(e.value) ? e.value : [])}
                display="comma"
                maxSelectedLabels={2}
                selectedItemsLabel="{0} desks"
                showSelectAll={allowedDesks.length > 2}
                selectAllLabel={controlDesks.length === allowedDesks.length ? "Deselect all" : "Select all"}
                panelFooterTemplate={
                  <div style={{ padding: "0.5rem 0.75rem", fontSize: "0.75rem", opacity: 0.7 }}>
                    {homeDesk ? `${homeDesk} (your desk) stays selected when you clear` : "At least one desk stays selected"}
                  </div>
                }
                aria-label="Control desks"
                style={{ maxWidth: "18rem" }}
              />
            </li>
          )}
          <WhatsNewButton />
          <NotificationBell />
          <li className={profileItemClassName}>
            <button type="button" className="p-link" onClick={onTopbarUserMenu}>
              <img src={user?.picture} alt="diamond-layout" className="profile-image" />

              <span id="user-id" className="profile-name">
                {user?.name}
              </span>
            </button>
            <ul className="profile-menu fade-in-up">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="theme1">Colour Scheme:</label>
              <br />
              <li>
                <RadioButton
                  name="colorScheme"
                  value="dark"
                  checked={theme === "dark"}
                  inputId="theme1"
                  onChange={(e) => changeTheme(e.value)}
                  style={{ margin: "5px" }}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="theme1">Dark</label>
              </li>

              <li>
                <RadioButton
                  name="colorScheme"
                  value="dim"
                  checked={theme === "dim"}
                  inputId="theme2"
                  style={{ margin: "5px" }}
                  onChange={(e) => changeTheme(e.value)}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="theme2">Dim</label>
              </li>
              <li>
                <RadioButton
                  name="colorScheme"
                  value="light"
                  style={{ margin: "5px" }}
                  checked={theme === "light"}
                  inputId="theme3"
                  onChange={(e) => changeTheme(e.value)}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="theme3">Light</label>
              </li>
              <br />

              {/* TODO: SETTINGS hidden until further notice */}
              {/* <li>
                <Link to="/settings">
                  <i className="pi pi-cog" />
                  <span>Settings</span>
                </Link>
              </li> */}

              <li>
                <button
                  id="logout-button"
                  type="button"
                  className="p-link"
                  onClick={() => {
                    // the next visitor of this browser must not boot into this account's app
                    clearBootSnapshot()
                    logout({ logoutParams: { returnTo: window.location.origin } })
                  }}
                >
                  <i className="pi pi-power-off" />
                  <span>Logout</span>
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  )
}

export default AppTopbar
